'use strict';

const i18n = require('i18next');
const Mn = require('backbone.marionette');
const Snackbar = require('common/util/snackbar');
const Dialog = require('common/util/dialog');
const ReadMore = require('common/ui/view/behavior/readmore');
const ModalView = require('common/ui/view/modal/index');
const GroupMemberNameView = require('common/ui/view/group/member-name');
const ContentEditView = require('data/ui/view/modal/content-edit');
const CommentsCounterView = require('communication/ui/comment/view/counter');
const TasksCounterView = require('communication/ui/view/task/counter');
const ContentActionWorkUpdateService = require('balus/app/service/actionwork/content/update');
const ContentActionWorkRelationService = require('balus/app/service/actionwork/content/relation');
const ContentActionWorkDeleteService = require('balus/app/service/actionwork/content/delete');
const ContentActionWorkRepository = require('balus/infra/repository/actionwork/content');
const RelationView = require('balus/ui/view/body/process/content/relation');
const Template = require('balus/ui/template/body/process/content/content-item.hbs');
const OpenURI = require('data/ui/view/content/open-uri');

const ItemView = Mn.View.extend({
    tagName: 'li',
    className: 'list-item',
    ui: {
        editButton: '.edit',
        relationButton: '.relation',
        deleteButton: '.delete',
        addComment: '.add-comment',
        addTask: '.add-task',
    },
    events: {
        'click @ui.editButton': 'edit',
        'click @ui.relationButton': 'updateRelation',
        'click @ui.deleteButton': 'delete',
        'click @ui.addComment': 'addComment',
        'click @ui.addTask': 'addTask',
    },
    modelEvents: {
        'change': 'render'
    },
    template: Template,
    regions: {
        commentsCounter: '.comments-counter',
        tasksCounter: '.tasks-counter',
        updatedBy: '.updatedBy',
        uri: '.uri'
    },
    templateContext: function() {
        const context = this.getOption('context');
        const user = context.getUser();
        const group = context.getGroup();
        const actionWork = this.model;
        const work = actionWork.getWork();
        const latestVersion = work.getTarget().getLatestVersion();
        const contentType = latestVersion.getMetaData().getContentType();

        return {
            name: work.getName(),
            latestVersion: latestVersion.toJSON(),
            contentType: contentType,
            path: work.getTarget().path(),
            input: actionWork.isInput(),
            output: actionWork.isOutput(),
            isMember: group.isMember(user ? user.id : null),
            isEmpty: latestVersion.isEmpty()
        }
    },

    initialize: function() {
        this._isShowVersions = false;
        this.listenTo(this.model.getWork().getTarget(), 'change', this.render);
    },

    onRender: function() {
        const context = this.getOption('context');
        const project = context.getProject();
        const work = this.model.getWork();
        const content = work.getTarget();
        const latestVersion = content.getLatestVersion();
        const updatedBy = latestVersion.getUpdatedBy();

        if(this._isShowVersions) {
            this.showVersions();
        }
        if(updatedBy) {
            this.showChildView('updatedBy', new GroupMemberNameView({model: updatedBy}));
        }

        this.showChildView('uri', new OpenURI({
            version: latestVersion,
            project: project,
        }));

        this.showChildView('commentsCounter', new CommentsCounterView({
            icon: 'fa fa-comments-o',
            project: project,
            key: content.key,
            hasComment: content.hasComment(),
        }));

        this.showChildView('tasksCounter', new TasksCounterView({
            icon: 'fa fa-check-square',
            project: project,
            key: content.key,
            hasTask: content.hasTask(),
        }));

        return this;
    },

    edit: function() {
        const project = this.getOption('project');
        const action = this.getOption('action');
        const content = this.model.getWork().getTarget();
        const relation = this.model.get('relation');
        const modal = new ModalView({
            title: i18n.t('data:edit.title'),
            bodyView: new ContentEditView({
                model: content,
                project: project
            }),
            onChildviewSave: function() {
                const contentJSON = this.getChildView('body').getContent();
                this.disableButtons();
                (new ContentActionWorkUpdateService(
                    project, action, content, contentJSON, relation))
                    .update()
                    .then(() => {
                        this.hide();
                    });
            }
        });
        modal.show();
    },

    updateRelation: function() {
        const modal = new ModalView({
            model: this.model,
            title: i18n.t('balus_template_body_process:modal.relation.title', {
                name: this.model.getAction().getName(),
                interpolation: { escapeValue: false }
            }),
            bodyView: new RelationView({model: this.model}),
            saveButtonEnable: true,
            onChildviewSave: function () {
                const relation = this.getChildView('body').getRelation();
                this.disableButtons();
                (new ContentActionWorkRelationService(this.model, relation))
                    .update()
                    .then(() => {
                        this.hide();
                    })
                    .catch(() => {
                        this.enableButtons();
                    });
            }
        });
        modal.show();
    },

    delete: function() {
        const project = this.getOption('project');
        const actionWork = this.model;
        const content = actionWork.getWork().getTarget();

        Dialog.confirm({
            text: i18n.t('balus_view_body_process:content.data-content.dialog.deleteConfirm', {name: content.getName()}),
            okCallback: () => {
                (new ContentActionWorkDeleteService(
                    project, actionWork))
                    .delete()
                    .then(() => {
                        this.trigger('remove:actionWork', actionWork);
                        Snackbar.success(
                            i18n.t('balus_view_body_process:content.data-content.feedback.delete', {name: content.getName()})
                        );
                    });
            }
        })
    },

    addComment: function() {
        const content = this.model.getWork().getTarget();
        this.trigger('add:comment', content);
    },

    addTask: function() {
        const content = this.model.getWork().getTarget();
        this.trigger('add:task', content);
    },
});

module.exports = Mn.CollectionView.extend({
    tagName: 'ul',
    className: 'list',
    childView: ItemView,
    childViewOptions: function() {
        return {
            context: this.getOption('context'),
            project: this.model.getProject(),
            action: this.model
        }
    },
    childViewTriggers: {
        'add:comment': 'content:add:comment',
        'add:task': 'content:add:task',
        'change': 'render',
    },

    behaviors: {
        ReadMore: {
            behaviorClass: ReadMore,
            showReadMore: function() {
                return ContentActionWorkRepository.find(this.model.getProject())
                    .hasNextActionWorksByAction(this.model);
            },
            onReadMore: function() {
                // 続きを読む、をクリックされたときには、このアクションに関連付けられている全ての
                // Content-Work を取得する
                return ContentActionWorkRepository.find(this.model.getProject())
                    .fetchAllActionWorkContentsByAction(this.model);
            }
        }
    },

    filter: function(child, index, collection) {
        return !child.getWork().getTarget().isDeleted();
    },

    onChildviewRemoveActionWork: function(actionWork) {
        this.collection.remove(actionWork);
    },
});



// WEBPACK FOOTER //
// ./balus/ui/view/body/process/content/content-work.js